import {
    explanationElementDataSchema,
    explanationElementSchema,
} from "@/Components/Exercises/Elements/Explanation/explanationSchema";
import {
    infoElementDataSchema,
    infoElementSchema,
} from "@/Components/Exercises/Elements/Info/infoSchema";
import {
    likertListElementDataSchema,
    likertListElementSchema,
} from "@/Components/Exercises/Elements/LikertList/likertListSchema";
import {
    multipleChoiceElementDataSchema,
    multipleChoiceElementSchema,
} from "@/Components/Exercises/Elements/MultipleChoice/multipleChoiceSchema";
import {
    orderedChoiceElementDataSchema,
    orderedChoiceElementSchema,
} from "@/Components/Exercises/Elements/OrderedChoice/orderedChoiceSchema";
import {
    singleChoiceElementDataSchema,
    singleChoiceElementSchema,
} from "@/Components/Exercises/Elements/SingleChoice/singleChoiceSchema";
import {
    textAreaElementDataSchema,
    textAreaElementSchema,
} from "@/Components/Exercises/Elements/TextArea/textAreaSchema";
import {
    booleanReturnOpSchema,
    stringReturnOpSchema,
} from "@/Components/Exercises/LogicOperation";
import { z } from "zod";
import {
    buttonTo360ElementDataSchema,
    buttonTo360ElementSchema,
} from "./Elements/ButtonTo360/buttonTo360schema";
import {
    radarChartElementDataSchema,
    radarChartElementSchema,
} from "./Elements/RadarChart/radarChartSchema";
import {
    writeMultipleTextsElementDataSchema,
    writeMultipleTextsElementSchema,
} from "./Elements/WriteMultipleTexts/writeMultipleTextsSchema";

export const exerciseElementSchema = z.discriminatedUnion("type", [
    infoElementSchema,
    multipleChoiceElementSchema,
    singleChoiceElementSchema,
    textAreaElementSchema,
    orderedChoiceElementSchema,
    explanationElementSchema,
    likertListElementSchema,
    radarChartElementSchema,
    writeMultipleTextsElementSchema,
    buttonTo360ElementSchema,
]);

// Map of element types to their data schemas
export const elementDataMapSchema = z.object({
    info: infoElementDataSchema,
    singleChoice: singleChoiceElementDataSchema,
    multipleChoice: multipleChoiceElementDataSchema,
    textArea: textAreaElementDataSchema,
    orderedChoice: orderedChoiceElementDataSchema,
    explanation: explanationElementDataSchema,
    likertList: likertListElementDataSchema,
    radarChart: radarChartElementDataSchema,
    writeMultipleTexts: writeMultipleTextsElementDataSchema,
    buttonTo360: buttonTo360ElementDataSchema,
});

export const elementGroupSchema = z.object({
    id: z.string(),
    type: z.literal("group"),
    onlyShowIf: booleanReturnOpSchema.optional(),
    canGoNext: booleanReturnOpSchema.optional(),
    elements: z.array(exerciseElementSchema),
});

const coachNoteSchema = z.object({
    content: z.union([stringReturnOpSchema, z.string()]),
    onlyShowIf: z.union([booleanReturnOpSchema, z.boolean()]).optional(),
    accordionSettings: z
        .object({
            title: z.union([stringReturnOpSchema, z.string()]),
            startExpanded: z
                .union([booleanReturnOpSchema, z.boolean()])
                .optional(),
        })
        .optional(),
    // Zod 4 evaluates a getter lazily, which is what lets a schema reference itself.
    get coachNotes() {
        return z.array(coachNoteSchema).optional();
    },
});

export const coachNotesSchema = z.array(coachNoteSchema);

export type CoachNote = z.infer<typeof coachNoteSchema>;

export const exerciseSchema = z.object({
    id: z.number(),
    key: z.string(),
    name: z.string(),
    icon: z.string(),
    description: z.string(),
    about: z.string(),
    who_is_it_for: z.string(),
    the_science_behind_it: z.string(),
    sample_questions: z.array(z.string()).nullable(),
    citations: z.string(),
    sections: z.array(
        z
            .union([exerciseElementSchema, elementGroupSchema])
            .and(z.object({ coachNotes: coachNotesSchema.optional() })),
    ),
    results_summary_for_ai_assembly_instructions: z
        .union([stringReturnOpSchema, z.string()])
        .optional()
        .nullable(),
    is_bookmarked: z.boolean().optional(),
    estimated_minutes: z.number().nullable().optional(),
    categories: z.union([
        z.undefined(),
        z.array(
            z.object({
                id: z.number(),
                pretty_name: z.string(),
                key: z.string(),
            }),
        ),
    ]),
});

export type ExerciseElement = z.infer<typeof exerciseElementSchema>;
export type ElementData<Elem extends ExerciseElement> = z.infer<
    typeof elementDataMapSchema
>[Elem["type"]];
export type Exercise = z.infer<typeof exerciseSchema>;

export type ExerciseData = Record<
    ExerciseElement["id"],
    ElementData<ExerciseElement>
>;

export enum ViewMode {
    Results = "results", // When reading the results of an exercise (coach viewing client's results). It should not be possible to edit the data.
    Preview = "preview", // When previewing an exercise (coach previewing client's exercise). It should be possible to edit the data, but the data is not saved.
    Edit = "edit", // When editing an exercise (client doing the exercise). It should be possible to edit the data, and the data is saved.
}

export function validateExerciseData(
    exercise: Exercise,
    data: ExerciseData,
): boolean {
    for (const element of exercise.sections) {
        if (element.type === "group") {
            for (const subElement of element.elements) {
                const elementData = data[subElement.id];
                if (elementData === undefined) {
                    continue;
                }
                elementDataMapSchema.shape[subElement.type].parse(elementData);
            }
        } else {
            const elementData = data[element.id];
            if (elementData === undefined) {
                continue;
            }
            elementDataMapSchema.shape[element.type].parse(elementData);
        }
    }
    return true;
}
